import { useState, useEffect, useCallback } from "react";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";
import Layout from "../components/Layout";

const APK_URL = import.meta.env.VITE_RIDER_APK_URL ||
  'https://appdistribution.firebase.google.com/testerapps/1:286935753493:android:42714d2476dc160991bf1c/releases/4ggg7kb9n4ruo?utm_source=firebase-console';

const VEHICLE_TYPES = [
  { value: "motorcycle", label: "🏍️ Motorcycle" },
  { value: "bicycle",    label: "🚲 Bicycle" },
  { value: "van",        label: "🚐 Van" },
  { value: "truck",      label: "🚛 Truck" },
  { value: "bus",        label: "🚌 Bus" },
  { value: "lorry",      label: "🚚 Lorry" },
];

const vehicleIcon = (type) => {
  const map = { motorcycle: "🏍️", bicycle: "🚲", van: "🚐", truck: "🚛", bus: "🚌", lorry: "🚚" };
  return map[type] || "🚗";
};

export default function DriversPage() {
  const { user } = useAuth();

  const [drivers, setDrivers]               = useState([]);
  const [loading, setLoading]               = useState(true);
  const [showModal, setShowModal]           = useState(false);
  const [showAppModal, setShowAppModal]     = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [stats, setStats]                   = useState({});
  const [loadingStats, setLoadingStats]     = useState({});
  const [toast, setToast]                   = useState(null);

  // Commission edit state
  const [editCommissionDriver, setEditCommissionDriver] = useState(null);
  const [commissionInput, setCommissionInput]           = useState("");
  const [commissionLoading, setCommissionLoading]       = useState(false);
  const [commissionError, setCommissionError]           = useState("");

  const [form, setForm] = useState({
    name: "", phone: "", password: "", vehicle_type: "motorcycle", commission_rate: "",
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError]     = useState("");

  const planLimit   = user?.shop?.driver_limit ?? 3;
  const driverCount = user?.shop?.driver_count ?? drivers.length;
  const atLimit     = planLimit !== -1 && driverCount >= planLimit;
  const shopCommissionRate = user?.shop?.commission_rate;

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchDrivers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/riders");
      setDrivers(res.data);
    } catch {
      showToast("Failed to load drivers.", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDrivers(); }, [fetchDrivers]);

  const fetchStats = async (driverId) => {
    if (stats[driverId] || loadingStats[driverId]) return;
    setLoadingStats((prev) => ({ ...prev, [driverId]: true }));
    try {
      const res = await api.get(`/riders/${driverId}/stats`);
      setStats((prev) => ({ ...prev, [driverId]: res.data }));
    } catch {
      showToast("Could not load driver stats.", "error");
    } finally {
      setLoadingStats((prev) => ({ ...prev, [driverId]: false }));
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (atLimit) { setShowModal(false); setShowUpgradeModal(true); return; }
    setFormError("");
    setFormLoading(true);
    try {
      await api.post("/riders", { ...form, role: "rider" });
      setShowModal(false);
      setForm({ name: "", phone: "", password: "", vehicle_type: "motorcycle", commission_rate: "" });
      showToast("Driver created successfully.");
      fetchDrivers();
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.limit_reached) {
        setShowModal(false); setShowUpgradeModal(true);
      } else {
        setFormError(
          err.response?.data?.message ||
          Object.values(err.response?.data?.errors || {}).flat().join(" ") ||
          "Failed to create driver."
        );
      }
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggle = async (driver) => {
    try {
      await api.patch(`/riders/${driver.id}/toggle`);
      setDrivers((prev) =>
        prev.map((d) => d.id === driver.id ? { ...d, is_active: !d.is_active } : d)
      );
      showToast(driver.is_active ? "Driver suspended." : "Driver activated.");
    } catch {
      showToast("Action failed.", "error");
    }
  };

  const openEditCommission = (driver) => {
    setEditCommissionDriver(driver);
    setCommissionInput(driver.commission_rate !== null && driver.commission_rate !== undefined
      ? String(driver.commission_rate) : "");
    setCommissionError("");
  };

  const handleUpdateCommission = async () => {
    setCommissionError("");
    setCommissionLoading(true);
    try {
      await api.patch(`/riders/${editCommissionDriver.id}/commission`, {
        commission_rate: commissionInput === "" ? null : Number(commissionInput),
      });
      setDrivers((prev) =>
        prev.map((d) =>
          d.id === editCommissionDriver.id
            ? { ...d, commission_rate: commissionInput === "" ? null : Number(commissionInput) }
            : d
        )
      );
      // Clear cached stats so they reload with new commission
      setStats((prev) => { const s = { ...prev }; delete s[editCommissionDriver.id]; return s; });
      showToast("Commission updated successfully.");
      setEditCommissionDriver(null);
    } catch (err) {
      setCommissionError(
        err.response?.data?.message ||
        Object.values(err.response?.data?.errors || {}).flat().join(" ") ||
        "Failed to update commission."
      );
    } finally {
      setCommissionLoading(false);
    }
  };

  const activeCount    = drivers.filter((d) => d.is_active).length;
  const suspendedCount = drivers.filter((d) => !d.is_active).length;

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');`}</style>

        {/* TOAST */}
        {toast && (
          <div className={`fixed top-5 right-5 z-50 flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-xl text-sm font-medium transition-all ${
            toast.type === "error" ? "bg-red-500 text-white" : "bg-green-500 text-white"
          }`}>
            {toast.type === "error" ? "❌" : "✅"} {toast.message}
          </div>
        )}

        {/* HEADER */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Sora', sans-serif" }}>
              Drivers
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              {activeCount} active · {suspendedCount} suspended ·{" "}
              <span className={atLimit ? "text-red-500 font-semibold" : "text-gray-500"}>
                {driverCount}/{planLimit === -1 ? "∞" : planLimit} drivers used
              </span>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowAppModal(true)}
              className="inline-flex items-center gap-2 border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium px-4 py-2.5 rounded-xl transition-colors"
            >
              📱 Driver App
            </button>
            <button
              onClick={() => atLimit ? setShowUpgradeModal(true) : setShowModal(true)}
              className={`inline-flex items-center gap-2 font-semibold px-4 py-2.5 rounded-xl transition-all text-white ${
                atLimit ? "bg-gray-400 cursor-not-allowed" : "bg-orange-500 hover:bg-orange-600 shadow-md shadow-orange-200"
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
              {atLimit ? "⚠️ Limit Reached" : "Add Driver"}
            </button>
          </div>
        </div>

        {/* STATS CARDS */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Total Drivers", value: drivers.length,   icon: "👥", color: "bg-blue-50 text-blue-600" },
            { label: "Active",        value: activeCount,      icon: "✅", color: "bg-green-50 text-green-600" },
            { label: "Suspended",     value: suspendedCount,   icon: "🚫", color: "bg-red-50 text-red-600" },
            {
              label: "Plan Limit",
              value: planLimit === -1 ? "∞" : `${driverCount}/${planLimit}`,
              icon:  atLimit ? "⚠️" : "📋",
              color: atLimit ? "bg-red-50 text-red-600" : "bg-purple-50 text-purple-600",
            },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg mb-3 ${s.color}`}>
                {s.icon}
              </div>
              <p className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Sora', sans-serif" }}>
                {s.value}
              </p>
              <p className="text-gray-500 text-xs mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        {/* UPGRADE BANNER */}
        {atLimit && (
          <div className="mb-6 bg-orange-50 border border-orange-200 rounded-2xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div>
              <p className="font-semibold text-orange-800 text-sm">
                🚀 You've reached your driver limit ({driverCount}/{planLimit})
              </p>
              <p className="text-orange-600 text-xs mt-0.5">Upgrade your plan to add more drivers.</p>
            </div>
            <button
              onClick={() => setShowUpgradeModal(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors shrink-0"
            >
              Upgrade Plan
            </button>
          </div>
        )}

        {/* DRIVERS GRID */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400 gap-3">
            <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
            </svg>
            Loading drivers...
          </div>
        ) : drivers.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <p className="text-5xl mb-4">🏍️</p>
            <p className="font-semibold text-gray-600 text-lg">No drivers yet</p>
            <p className="text-sm mt-1 mb-5">Add your first driver to get started.</p>
            <button onClick={() => setShowModal(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-5 py-2.5 rounded-xl text-sm transition-colors">
              Add First Driver
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {drivers.map((driver) => {
              const dStats = stats[driver.id];
              const isLoadingStats = loadingStats[driver.id];

              return (
                <div key={driver.id}
                  className={`bg-white rounded-2xl border shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden ${
                    !driver.is_active ? "opacity-60" : "border-gray-100"
                  }`}
                >
                  {/* Card Header */}
                  <div className="p-5 border-b border-gray-50">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 bg-orange-100 rounded-xl flex items-center justify-center text-xl shrink-0">
                          {vehicleIcon(driver.vehicle_type)}
                        </div>
                        <div>
                          <p className="font-bold text-gray-900">{driver.name}</p>
                          <p className="text-gray-500 text-xs">{driver.phone}</p>
                        </div>
                      </div>
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full shrink-0 ${
                        driver.is_active
                          ? "bg-green-50 text-green-600 border border-green-100"
                          : "bg-red-50 text-red-500 border border-red-100"
                      }`}>
                        {driver.is_active ? "Active" : "Suspended"}
                      </span>
                    </div>

                    {/* Vehicle badge */}
                    <div className="mt-3 inline-flex items-center gap-1.5 bg-slate-50 border border-slate-100 text-slate-600 text-xs font-medium px-3 py-1 rounded-full capitalize">
                      {vehicleIcon(driver.vehicle_type)} {driver.vehicle_type || "Not set"}
                    </div>

                    {/* Commission row with edit button */}
                    <div className="mt-2 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400">Commission:</span>
                        <span className="text-xs font-semibold text-orange-600">
                          {driver.commission_rate != null
                            ? `${driver.commission_rate}% (custom)`
                            : shopCommissionRate
                              ? `${shopCommissionRate}% (shop default)`
                              : "100% (no deduction)"}
                        </span>
                      </div>
                      <button
                        onClick={() => openEditCommission(driver)}
                        className="text-xs text-blue-500 hover:text-blue-700 font-medium flex items-center gap-1 hover:bg-blue-50 px-2 py-1 rounded-lg transition-colors"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125" />
                        </svg>
                        Edit
                      </button>
                    </div>
                  </div>

                  {/* Stats Section */}
                  <div className="px-5 py-4">
                    {dStats ? (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-slate-50 rounded-xl p-3 text-center">
                          <p className="text-xl font-bold text-gray-900" style={{ fontFamily: "'Sora', sans-serif" }}>
                            {dStats.week_trips}
                          </p>
                          <p className="text-gray-500 text-xs mt-0.5">Trips this week</p>
                        </div>
                        <div className="bg-slate-50 rounded-xl p-3 text-center">
                          <p className="text-xl font-bold text-gray-900" style={{ fontFamily: "'Sora', sans-serif" }}>
                            {dStats.all_time_trips}
                          </p>
                          <p className="text-gray-500 text-xs mt-0.5">Total trips</p>
                        </div>
                        <div className="bg-orange-50 rounded-xl p-3 text-center">
                          <p className="text-base font-bold text-orange-600" style={{ fontFamily: "'Sora', sans-serif" }}>
                            KES {Number(dStats.weekly_earnings).toLocaleString()}
                          </p>
                          <p className="text-gray-500 text-xs mt-0.5">This week</p>
                        </div>
                        <div className="bg-orange-50 rounded-xl p-3 text-center">
                          <p className="text-base font-bold text-orange-600" style={{ fontFamily: "'Sora', sans-serif" }}>
                            KES {Number(dStats.monthly_earnings).toLocaleString()}
                          </p>
                          <p className="text-gray-500 text-xs mt-0.5">This month</p>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => fetchStats(driver.id)}
                        disabled={isLoadingStats}
                        className="w-full text-center text-sm text-orange-500 hover:text-orange-600 font-medium py-2 rounded-xl hover:bg-orange-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {isLoadingStats ? (
                          <>
                            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                            </svg>
                            Loading stats...
                          </>
                        ) : "Load earnings & trip stats →"}
                      </button>
                    )}
                  </div>

                  {/* Toggle Button */}
                  <div className="px-5 pb-5">
                    <button
                      onClick={() => handleToggle(driver)}
                      className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-all ${
                        driver.is_active
                          ? "bg-red-50 text-red-500 hover:bg-red-100 border border-red-100"
                          : "bg-green-50 text-green-600 hover:bg-green-100 border border-green-100"
                      }`}
                    >
                      {driver.is_active ? "🚫 Suspend Driver" : "✅ Activate Driver"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* EDIT COMMISSION MODAL */}
        {editCommissionDriver && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
              <div className="flex items-center justify-between p-6 border-b border-gray-100">
                <div>
                  <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: "'Sora', sans-serif" }}>
                    Edit Commission
                  </h2>
                  <p className="text-gray-500 text-sm mt-0.5">{editCommissionDriver.name}</p>
                </div>
                <button
                  onClick={() => setEditCommissionDriver(null)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6 space-y-4">
                {commissionError && (
                  <div className="bg-red-50 border border-red-100 text-red-600 text-sm rounded-xl p-3">
                    {commissionError}
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                    Commission Rate
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={commissionInput}
                      onChange={(e) => setCommissionInput(e.target.value)}
                      placeholder="Leave blank to use shop default"
                      min="0"
                      max="100"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                  </div>
                  <p className="text-gray-400 text-xs mt-1.5">
                    Current:{" "}
                    <span className="font-semibold text-orange-600">
                      {editCommissionDriver.commission_rate != null
                        ? `${editCommissionDriver.commission_rate}% (custom)`
                        : shopCommissionRate
                          ? `${shopCommissionRate}% (shop default)`
                          : "100% (no deduction)"}
                    </span>
                  </p>
                  <p className="text-gray-400 text-xs mt-1">
                    Leave blank to revert to shop default. This affects rider earnings calculations.
                  </p>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setEditCommissionDriver(null)}
                    className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateCommission}
                    disabled={commissionLoading}
                    className="flex-1 py-2.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2"
                  >
                    {commissionLoading ? (
                      <>
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                        </svg>
                        Saving...
                      </>
                    ) : "Save Commission"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ADD DRIVER MODAL */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
              <div className="flex items-center justify-between p-6 border-b border-gray-100">
                <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: "'Sora', sans-serif" }}>
                  Add New Driver
                </h2>
                <button onClick={() => { setShowModal(false); setFormError(""); }}
                  className="text-gray-400 hover:text-gray-600 transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-6 space-y-4">
                {formError && (
                  <div className="bg-red-50 border border-red-100 text-red-600 text-sm rounded-xl p-3">{formError}</div>
                )}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Full Name</label>
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. James Kamau"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Phone Number</label>
                  <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="07XXXXXXXX"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Vehicle Type</label>
                  <select value={form.vehicle_type} onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent bg-white">
                    {VEHICLE_TYPES.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Password</label>
                  <input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="Min. 8 characters"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Commission Rate (optional)</label>
                  <div className="relative">
                    <input type="number" value={form.commission_rate} onChange={(e) => setForm({ ...form, commission_rate: e.target.value })}
                      placeholder="Leave blank to use shop default" min="0" max="100"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                  </div>
                  <p className="text-gray-400 text-xs mt-1">Overrides the shop's global commission for this driver only</p>
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => { setShowModal(false); setFormError(""); }}
                    className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors">
                    Cancel
                  </button>
                  <button type="submit" onClick={handleCreate} disabled={formLoading}
                    className="flex-1 py-2.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2">
                    {formLoading ? (
                      <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                      </svg>Creating...</>
                    ) : "Create Driver"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* UPGRADE MODAL */}
        {showUpgradeModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm text-center p-8">
              <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center text-3xl mx-auto mb-5">🚀</div>
              <h2 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Sora', sans-serif" }}>Driver Limit Reached</h2>
              <p className="text-gray-500 text-sm mb-6 leading-relaxed">
                You've reached the maximum number of drivers on your current plan. Upgrade to add more drivers and unlock additional features.
              </p>
              <div className="space-y-3 mb-6 text-left">
                {[
                  { plan: "Growth",     price: "KES 2,499/mo", drivers: "Up to 10 drivers",  color: "border-purple-200 bg-purple-50" },
                  { plan: "Enterprise", price: "KES 5,999/mo", drivers: "Unlimited drivers", color: "border-orange-200 bg-orange-50" },
                ].map((p) => (
                  <div key={p.plan} className={`border rounded-xl p-4 ${p.color}`}>
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-bold text-gray-900 text-sm">{p.plan}</p>
                        <p className="text-gray-500 text-xs">{p.drivers}</p>
                      </div>
                      <p className="font-bold text-gray-900 text-sm">{p.price}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                <button onClick={() => setShowUpgradeModal(false)}
                  className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors">
                  Not Now
                </button>
                <a href="https://wa.me/254769177634?text=I'd like to upgrade my TumaTrack plan"
                  target="_blank" rel="noreferrer"
                  className="flex-1 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-sm font-semibold transition-colors text-center">
                  💬 Upgrade Now
                </a>
              </div>
            </div>
          </div>
        )}

        {/* DRIVER APP MODAL */}
        {showAppModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: "'Sora', sans-serif" }}>Tuma Rider App</h2>
                <button onClick={() => setShowAppModal(false)} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
              <div className="text-center mb-5">
                <div className="w-20 h-20 bg-orange-50 rounded-2xl flex items-center justify-center text-4xl mx-auto mb-3">🏍️</div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Share this app with your riders. They can download and install it directly on their Android phones.
                </p>
              </div>
              <div className="space-y-3">
                <a href={APK_URL} target="_blank" rel="noreferrer"
                  className="flex items-center justify-center gap-2 w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 rounded-xl transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"/>
                  </svg>
                  Download Tuma Rider App
                </a>
                <button onClick={() => { navigator.clipboard.writeText(APK_URL); showToast("Download link copied!"); setShowAppModal(false); }}
                  className="flex items-center justify-center gap-2 w-full border border-gray-200 text-gray-600 hover:bg-gray-50 font-medium py-3 rounded-xl transition-colors text-sm">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 7.5V6.108c0-1.135.845-2.098 1.976-2.192.373-.03.748-.057 1.123-.08M15.75 18H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08M15.75 18.75v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5A3.375 3.375 0 006.375 7.5H5.25m11.9-3.664A2.251 2.251 0 0015 2.25h-1.5a2.251 2.251 0 00-2.15 1.586m5.8 0c.065.21.1.433.1.664v.75h-6V4.5c0-.231.035-.454.1-.664"/>
                  </svg>
                  Copy Link to Share via WhatsApp
                </button>
              </div>
              <div className="mt-4 bg-blue-50 border border-blue-100 rounded-xl p-4">
                <p className="text-blue-700 text-xs font-semibold mb-1">📋 Rider Install Instructions</p>
                <ol className="text-blue-600 text-xs space-y-1 list-decimal list-inside">
                  <li>Open the download link on their Android phone</li>
                  <li>Install Firebase App Tester if prompted</li>
                  <li>Download and install Tuma Rider</li>
                  <li>Enable "Install from unknown sources" if asked</li>
                  <li>Log in with their phone and password</li>
                </ol>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}